import { Component, Output, EventEmitter, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { zip } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';

import Quagga from 'quagga/dist/quagga';

import { initAutocomplete, initAutocomplete_s, restrictCountry, buildSenderAddress } from '../new-parcel/autocompleteAddress';
import { GridOptions } from "ag-grid-community/main";
import { DataService } from './../data.service';
import { SlideInOutAnimation } from './../animations';
import { Parcel } from './../parcel-model';
import { NumericEditor } from './../numeric-editor.component';
import { DropdownEditor, DropdownRenderer } from './../dropdown-editor.component';

declare var $: any;

@Component({
  selector: 'app-new-money',
  animations: [SlideInOutAnimation],
  templateUrl: './new-money.component.html',
  styleUrls: ['./new-money.component.css']
})
export class NewMoneyComponent implements OnInit, OnDestroy, AfterViewInit {

  // ── Page 2 (MoneyInit) form model ──────────────────────────────────────────
  moneyInit: any = { rcountry: '', roblast: '', amount: null, discount: '' };
  oblastOptions: any[] = [];
  showOblast = false;
  discountError = '';

  // ── Page 4 (Sender / Receiver) form model ──────────────────────────────────
  addressData: any = {
    sphone: '', semail: '', sfname: '', slname: '',
    scountry: '', soblast: '', scity: '', sstreet: '', sstreetn: '', sapartn: '', szip: '', sregion: '',
    rphone: '', remail: '', rfname: '', rlname: '',
    rcountry: '', roblast: '', rcity: '', rstreet: '', rstreetn: '', rapartn: '', rzip: '', rregion: ''
  };
  senderProvinceOptions: any[] = [];
  recipientOblastReadonly = false;
  recipientEmailRequired = false;

  // ── Page 5 (Compliance) form model ────────────────────────────────────────
  complianceData: any = { occupation: '', occupationOther: '', tr_purp: '', thdprttr: '', onbehalfof: '', sourcefund: '' };
  occupationOptions: any[] = [];
  purposeOptions: any[] = [];
  showOnBehalfOf = false;
  showOccupationOther = false;

  // ── Page 7 (MoneyDetails) review display values ────────────────────────────
  reviewData: any = { notes: '', declaration: '' };
  reviewAmount = '';
  reviewDelivery = '';
  reviewPayAmount = '';

  // ── Shared wizard state ────────────────────────────────────────────────────
  countries: any[];
  countryStates: any[];
  oblasts: any[];
  deliverySelection: any[];
  itemGroups: any[];
  txtOK = 'OK';
  isMobile: boolean;
  discountID: number;
  address: any;
  pageno: number;
  page1: any;
  page2: any;
  page3: any;
  page4: any;
  pageMap: any;
  page5: any;
  animationState = ['in', 'out', 'out', 'out', 'out', 'out', 'out', 'out'];
  pageTitle: string;
  showScanner = false;
  scannedData: string;
  errorMessage: string;
  kycMessage: string;

  @Output() onSubmitted = new EventEmitter<Parcel>();
  @ViewChild('holdModal', { static: false }) holdModal;
  @ViewChild('brokLimit', { static: false }) brokLimit;
  bsModalRef: BsModalRef;
  maxAmount = 1000;
  config: any = {};

  // ── KYC page state ─────────────────────────────────────────────────────────
  submitting = false;

  kycRefId: string = null;
  kycActivityId: string = null;
  kycCaptureUrl: SafeResourceUrl = null;
  kycResult: any = null;
  kycResultError = '';
  kycWaitingForResult = false;
  kycRequired = true;
  private kycPollHandle: any = null;
  private kycFallbackHandle: any = null;
  private kycFallbackAttempts = 0;
  private readonly KYC_FALLBACK_MAX = 8;
  private readonly KYC_FALLBACK_MS = 15000;

  private grid2Options: GridOptions;
  public rowData2: any[];
  private columnDefs2: any[];
  private columnDefs2Freq: any[];
  private frameworkComponents2;
  private grid2Api;

  private readonly sender_map = {
    sapartn: 'u_apart', scity: 'u_city', scountry: 'u_country', semail: 'u_email',
    sfname: 'u_firstnm', slname: 'u_lastnm', soblast: 'u_oblast', sphone: 'u_phone',
    sregion: 'u_region', sstreet: 'u_street', sstreetn: 'u_house', szip: 'u_index'
  };

  constructor(private dataService: DataService, private route: ActivatedRoute,
              private router: Router, private modalService: BsModalService,
              private sanitizer: DomSanitizer) {
    this.isMobile = this.dataService.isMobileDevice;
    if (!this.isMobile && window.innerWidth < 992)
      this.isMobile = true;

    this.grid2Options = <GridOptions>{ columnDefs: this.columnDefs2 };
    this.frameworkComponents2 = { numericEditor: NumericEditor, dropdownEditor: DropdownEditor, dropdownRenderer: DropdownRenderer };

    this.columnDefs2 = [
      {
        headerName: 'Group Name', field: 'groupname', minWidth: 200, colId: 'Col1',
        editable: true,
        cellRenderer: 'dropdownRenderer',
        cellRendererParams: { options: [] },
        cellEditor: 'dropdownEditor',
        cellEditorParams: { options: [] }
      },
      { headerName: 'Quantity', editable: true, cellEditor: 'numericEditor', field: 'quantity', minWidth: 90, headerClass: 'leftHeader', cellClass: 'leftText', cellEditorParams: (par) => { return { value: par.value, focus: true }; } },
      { headerName: 'Description', editable: true, cellEditor: 'agTextCellEditor', field: 'description', minWidth: 110 },
      { headerName: 'Weight', editable: true, cellEditor: 'numericEditor', field: 'weight', type: 'numericColumn', minWidth: 90, cellEditorParams: (par) => { return { value: par.value, focus: false }; } },
      { headerName: 'Value', editable: true, cellEditor: 'numericEditor', field: 'value', type: 'numericColumn', minWidth: 80, cellEditorParams: (par) => { return { value: par.value, focus: false }; } },
    ];

    this.columnDefs2Freq = [
      { headerName: 'Group Name', field: 'groupname', minWidth: 200, editable: false, colId: 'Col1Freq' },
      { headerName: 'Quantity', editable: true, cellEditor: 'numericEditor', field: 'quantity', minWidth: 90, headerClass: 'leftHeader', cellClass: 'leftText', cellEditorParams: (par) => { return { value: par.value, focus: true }; } },
      { headerName: 'Description', editable: true, cellEditor: 'agTextCellEditor', field: 'description', minWidth: 110 },
      { headerName: 'Weight', editable: true, cellEditor: 'numericEditor', field: 'weight', type: 'numericColumn', minWidth: 90, cellEditorParams: (par) => { return { value: par.value, focus: false }; } },
      { headerName: 'Value', editable: true, cellEditor: 'numericEditor', field: 'value', type: 'numericColumn', minWidth: 80, cellEditorParams: (par) => { return { value: par.value, focus: false }; } },
    ];

    this.rowData2 = [{ groupname: '', description: '', weight: '', value: '', quantity: '', seqno: 0 }];

    zip(
      this.dataService.getItemGroups(),
      this.dataService.getMoneyConfig(),
      this.dataService.getMoneyOccupations(),
      this.dataService.getMoneyPurposes()
    ).subscribe((ui: any[]) => {
      this.itemGroups = ui[0];
      this.config = ui[1];
      this.maxAmount = this.config['MAXAMOUNT'];
      this.occupationOptions = ui[2] || [];
      this.purposeOptions = ui[3] || [];

      let dd = this.columnDefs2.find(x => x.field == 'groupname');
      dd.cellEditorParams.options = ui[0].map(o => { return { key: o.Value, value: o.Text }; });
      dd.cellRendererParams.options = dd.cellEditorParams.options;
    });
  }

  ngOnInit() {
    this.setupQuagga();
    this.route.url.subscribe(url => {
      console.log(url);
      this.init();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => { this.holdModal.show(); });
  }

  ngOnDestroy() {
    this.stopKycPolling();
  }

  init() {
    this.pageno = -1;
    this.animationState = ['in', 'out', 'out', 'out', 'out', 'out', 'out', 'out'];
    this.txtOK = 'Next';
    this.pageTitle = '';
    this.discountID = null;
    this.errorMessage = '';
    this.rowData2 = [{ groupname: '', description: '', weight: '', value: '', quantity: '', seqno: 0 }];
    this.complianceData = { occupation: '', occupationOther: '', tr_purp: '', thdprttr: '', onbehalfof: '', sourcefund: '' };
    this.showOnBehalfOf = false;
    this.showOccupationOther = false;
  }

  goahead() {
    zip(this.dataService.getCountryState(), this.dataService.getAgent('MEEST')).subscribe((res: any[]) => {
      this.countryStates = res[0];
      let agent_addrComp = {};
      let user_addrComp = {};
      this.dataService.parseAgentAddress(res[1], agent_addrComp);
      this.dataService.parseUserAddress(user_addrComp);
      this.pageno = 1;
      this.submitted(JSON.stringify({ userAddress: user_addrComp, agentAddress: agent_addrComp, agent: res[1] }));
    });
  }

  // ── Page 2: Recipient country changed ──────────────────────────────────────
  onRcountryChange() {
    const val = this.moneyInit.rcountry;
    this.moneyInit.roblast = '';
    if (!val) { this.showOblast = false; return; }
    const selc = this.countries.find(x => x.CountryCodeValue == val);
    if (selc && selc.CountryCodeType === 'DropDownTable') {
      this.dataService.getOblast(selc.r_country).subscribe((obl: any[]) => {
        this.oblasts = obl;
        this.oblastOptions = obl.map(c => ({ key: c.Id, value: c.r_region }));
        this.showOblast = true;
      });
    } else {
      this.oblasts = [];
      this.oblastOptions = [];
      this.showOblast = false;
    }
  }

  // ── Page 4: Sender country changed ─────────────────────────────────────────
  onScountryChange() {
    const val = (this.addressData.scountry || '').toUpperCase();
    if (val && this.countryStates && this.countryStates[val]) {
      this.senderProvinceOptions = this.countryStates[val].map(c => ({ key: c.state, value: c.desc }));
    } else {
      this.senderProvinceOptions = [];
    }
  }

  // ── Page 4: Sender phone lookup ────────────────────────────────────────────
  onSphoneBlur() {
    const phone = this.addressData.sphone;
    if (!phone) return;
    this.dataService.getPhoneSender(phone).subscribe((addr: any) => {
      if (!addr) return;
      this.addressData.sphone = addr.s_phone;
      this.addressData.semail = addr.s_email;
      this.addressData.sfname = addr.s_firstnm;
      this.addressData.slname = addr.s_lastnm;
      if (!this.dataService.isGuest) {
        this.addressData.sapartn = addr.s_apart;
        this.addressData.scity = addr.s_city;
        this.addressData.scountry = (addr.s_country || '').toUpperCase();
        this.addressData.sstreetn = addr.s_house;
        this.addressData.szip = addr.s_index;
        this.addressData.soblast = addr.s_oblast;
        this.addressData.sstreet = addr.s_street;
        this.onScountryChange();
      }
    });
  }

  // ── Page 4: Recipient phone lookup ─────────────────────────────────────────
  onRphoneChange(value: string) {
    if (!value || value.length < 10) return;
    this.dataService.getPhoneRecipient(value).subscribe((addr: any) => {
      if (!addr) return;
      this.addressData.rphone = addr.r_phone;
      this.addressData.remail = addr.r_email;
      this.addressData.rfname = addr.r_firstnm;
      this.addressData.rlname = addr.r_lastnm;
      if (!this.page1.pickupAddress) {
        this.addressData.rapartn = addr.r_apart;
        this.addressData.rcity = addr.r_city;
        this.addressData.rstreetn = addr.r_house;
        this.addressData.rregion = addr.r_region;
        this.addressData.rstreet = addr.r_street;
      }
    });
  }

  // ── Google Maps autocomplete callback ──────────────────────────────────────
  // Called by autocompleteAddress.js with either (country, stateprov, null) for
  // sender country/province, or (null, null, elems[]) for individual field fills.
  updateStateList(country, stateprov, elems) {
    if (elems) {
      elems.forEach(x => this.addressData[x.key] = x.value);
    } else {
      if (!country || !this.countryStates || !this.countryStates[country.toUpperCase()]) return;
      this.addressData.scountry = country.toUpperCase();
      this.addressData.soblast = stateprov;
      this.onScountryChange();
      // Normalise stored province code
      const cl = this.countryStates[country.toUpperCase()];
      if (cl && stateprov) {
        const v = stateprov.toUpperCase();
        const sn = cl.find(x => x.state == v || x.desc.toUpperCase() == v);
        if (sn) this.addressData.soblast = sn.state;
      }
    }
    return false;
  }

  // ── Form submit helpers ────────────────────────────────────────────────────
  submitMoneyInit() {
    this.discountError = '';
    this.submitted(JSON.stringify(this.moneyInit));
  }

  submitAddress() {
    this.address = buildSenderAddress();
    this.submitted(JSON.stringify(this.addressData));
  }

  submitCompliance() {
    this.submitted(JSON.stringify(this.complianceData));
  }

  submitReview() {
    this.submitted(JSON.stringify(this.reviewData));
  }

  onOccupationChange() {
    const opt = this.occupationOptions.find(o => String(o.id) === String(this.complianceData.occupation));
    this.showOccupationOther = !!(opt && opt.text.toUpperCase() === 'OTHER');
    if (!this.showOccupationOther) this.complianceData.occupationOther = '';
  }

  onThdprttrChange() {
    this.showOnBehalfOf = this.complianceData.thdprttr === 'TPT';
    if (!this.showOnBehalfOf) this.complianceData.onbehalfof = '';
  }

  // ── KYC ID validation: server starts session, returns capture_url for the iframe.
  //    KYC pushes status/result via webhooks; client polls local DB for RESULT_RECEIVED.
  startKycValidation() {
    this.kycRefId = null;
    this.kycActivityId = null;
    this.kycCaptureUrl = null;
    this.kycResult = null;
    this.kycResultError = '';
    this.kycWaitingForResult = false;
    this.kycMessage = '';
    this.kycFallbackAttempts = 0;
    this.stopKycFallback();

    const userId = this.dataService.userData.UserData.id;

    this.dataService.startKycSession(
      userId,
      this.page3.semail,
      this.page3.sfname,
      this.page3.slname
    ).subscribe(
      (res: any) => {
        if (!res || !res.referenceId) {
          this.kycResultError = 'Failed to start ID validation session: missing reference id.';
          return;
        }
        this.kycRefId = res.referenceId;
        if (res.activity_id) this.kycActivityId = res.activity_id;
        if (res.capture_url) this.kycCaptureUrl = this.sanitizer.bypassSecurityTrustResourceUrl(res.capture_url);

        this.pollKycSession();
        this.kycPollHandle = setInterval(() => this.pollKycSession(), 5000);
      },
      (err: any) => {
        console.error('KYC session start error', err);
        this.kycResultError = 'Failed to start ID validation session.';
      }
    );
  }

  private pollKycSession() {
    if (!this.kycRefId) return;
    this.dataService.getKycSessionStatus(this.kycRefId).subscribe(
      (res: any) => {
        if (!res) return;
        if (res.activityId) this.kycActivityId = res.activityId;

        if (res.state === 'RESULT_RECEIVED' && res.result) {
          this.kycResult = res.result;
          this.kycWaitingForResult = false;
          this.stopKycPolling();
          return;
        }

        // Finalizing = end_function COMPLETED → hide iframe, show waiting message,
        // and arm the fallback timer in case the result webhook is delayed.
        if (!this.kycWaitingForResult && res.state === 'Finalizing') {
          this.kycWaitingForResult = true;
          this.startKycFallbackTimer();
        }
      },
      (err: any) => { console.error('KYC session status error', err); }
    );
  }

  kycStatusClass(status: string): string {
    if (!status) return 'text-muted';
    const s = status.toUpperCase();
    if (s === 'SUCCESS' || s === 'COMPLETE' || s === 'PASS' || s === 'OK') return 'text-success';
    if (s === 'FAIL' || s === 'FAILURE' || s === 'ERROR' || s === 'REJECTED') return 'text-danger';
    if (s === 'PENDING' || s === 'IN_PROGRESS') return 'text-warning';
    return 'text-muted';
  }

  // KYC page Next button: advance to review (only enabled when kycResult ready)
  submitKyc() {
    this.submitted(JSON.stringify({}));
  }

  // KYC page Cancel button: stop polling, return to home
  cancelKyc() {
    this.stopKycPolling();
    this.submitted('');
  }

  private stopKycPolling() {
    if (this.kycPollHandle) {
      clearInterval(this.kycPollHandle);
      this.kycPollHandle = null;
    }
    this.stopKycFallback();
  }

  private startKycFallbackTimer() {
    if (this.kycFallbackHandle) return;
    this.kycFallbackAttempts = 0;
    this.kycFallbackHandle = setInterval(() => this.kycFallbackPoll(), this.KYC_FALLBACK_MS);
  }

  private kycFallbackPoll() {
    if (this.kycResult) { this.stopKycFallback(); return; }
    this.kycFallbackAttempts++;
    this.dataService.fetchKycResult(this.kycRefId).subscribe(
      (res: any) => {
        if (res && res.result) {
          this.kycResult = res.result;
          this.kycWaitingForResult = false;
          this.stopKycPolling();
        } else if (this.kycFallbackAttempts >= this.KYC_FALLBACK_MAX) {
          this.stopKycFallback();
          this.kycResultError = 'ID verification result was not received. Please try again later or contact support.';
        }
      },
      (err: any) => {
        console.error('KYC fallback fetch error', err);
        if (this.kycFallbackAttempts >= this.KYC_FALLBACK_MAX) {
          this.stopKycFallback();
          this.kycResultError = 'ID verification result was not received. Please try again later or contact support.';
        }
      }
    );
  }

  private stopKycFallback() {
    if (this.kycFallbackHandle) {
      clearInterval(this.kycFallbackHandle);
      this.kycFallbackHandle = null;
    }
  }

  // ── Main wizard state machine ──────────────────────────────────────────────
  submitted(payLoad: string) {
    if (payLoad && payLoad !== '') {

      if (this.pageno == 1) {
        if (this.dataService.userData.UserData.u_type == 'CUSTOMER') {
          this.pageMap = JSON.parse(payLoad);
          this.dataService.userData.Branch = this.dataService.branches.find(x => x.id == this.pageMap.agent.branch);
        } else {
          this.pageMap = null;
        }

        this.dataService.getMoneyCountries().subscribe((res: any[]) => {
          this.countries = res;
          this.pageTitle = 'Enter Transfer Details';
          this.nextPage();
        });
      }

      if (this.pageno == 2) {
        this.page1 = JSON.parse(payLoad);

        if (Number(this.page1['amount']) > this.maxAmount) {
          this.brokLimit.show();
          return;
        }

        this.page1['rcountrycode'] = this.page1.rcountry;
        this.page1.rcountry = this.countries.find(x => x.CountryCodeValue == this.page1.rcountry).r_country;

        this.dataService.getDiscountsMT(null, this.dataService.userData.UserData.u_email, this.page1['discount']).subscribe((ui: any) => {
          if (this.page1['discount'] && !ui) {
            this.discountError = 'Invalid discount code';
            return;
          }
          this.discountError = '';
          if (this.page1['discount'] && ui)
            this.page1['discountData'] = ui;

          const param = { CountryCode: this.page1['rcountrycode'], Amount: this.page1['amount'], Currency: 'USD', Mode: 'I', UserID: this.dataService.userData.UserData.id };
          this.dataService.getMoneyTransferSelection(param).subscribe((ui2: any[]) => {
            if (ui2.length > 0 && ui2[0].TransferMode == 'Error')
              this.errorMessage = ui2[0].DeliveryModeDescription;
            else {
              this.errorMessage = '';
              this.deliverySelection = ui2;
              this.pageTitle = 'Select Transport and Delivery';
              this.nextPage();
            }
          });
        });
      }

      if (this.pageno == 3) {
        this.page2 = JSON.parse(payLoad);
        this.page2 = this.page2.delivery;

        if (this.page1['discountData']) {
          const disc = this.page1['discountData'];
          if (disc.DiscountType == '%')
            this.page2.TotalCustomerOwe_CAD = Math.round(this.page2.TotalCustomerOwe_CAD * (100 - disc.DiscountValue)) / 100;
          else
            this.page2.TotalCustomerOwe_CAD -= disc.DiscountValue;
          this.discountID = disc.id;
        }

        this.kycRequired = this.page2.KycRequired !== false;

        this.initAddressPage();
        this.pageTitle = 'Enter Address';
        this.nextPage();
      }

      if (this.pageno == 4) {
        this.page3 = JSON.parse(payLoad);

        this.reviewAmount = this.page1.amount + ' USD';
        this.reviewDelivery = this.page2.DeliveryModeDescription;
        this.reviewPayAmount = this.page2.TotalCustomerOwe_CAD + ' ' + this.dataService.userData.Branch.currency;

        if (this.kycRequired) {
          // Collect compliance info on page 5, then KYC on page 6.
          this.pageTitle = 'Compliance Information';
          this.nextPage();
        } else {
          // Skip compliance (page 5) and KYC (page 6), go straight to review (page 7).
          this.pageTitle = 'Complete and Review';
          this.skipPages(3);
        }
      }

      if (this.pageno == 5) {
        // Compliance page: kick off KYC session then advance to the KYC page.
        this.startKycValidation();
        this.pageTitle = 'ID Validation';
        this.nextPage();
      }

      if (this.pageno == 6) {
        // KYC page Next: just advance to the review page.
        this.pageTitle = 'Complete and Review';
        this.nextPage();
      }

      if (this.pageno == 7) {
        this.page4 = JSON.parse(payLoad);
        const model = this.buildModel(this.page1, this.page2, this.page3, this.page4, this.page5);
        this.errorMessage = '';
        this.submitting = true;
        this.dataService.saveMoney(model).subscribe((ui: number) => {
          model.id = ui;
          this.router.navigate(['payment', model.id, 1]);
        }, (err: any) => {
          this.submitting = false;
          this.errorMessage = this.dataService.processError(err);
        });
      }

    } else {
      this.stopKycPolling();
      this.animationState = ['out', 'out', 'out', 'out', 'out', 'out', 'out', 'out'];
      this.router.navigate(['home']);
    }
  }

  // ── Initialise the address page (called when entering page 4) ──────────────
  private initAddressPage() {
    this.addressData = {
      sphone: '', semail: '', sfname: '', slname: '',
      scountry: '', soblast: '', scity: '', sstreet: '', sstreetn: '', sapartn: '', szip: '', sregion: '',
      rphone: '', remail: '', rfname: '', rlname: '',
      rcountry: '', roblast: '', rcity: '', rstreet: '', rstreetn: '', rapartn: '', rzip: '', rregion: ''
    };
    this.recipientOblastReadonly = false;
    this.senderProvinceOptions = [];

    // Recipient country is read-only in money transfer
    this.addressData.rcountry = this.page1.rcountry;

    // Pre-fill recipient oblast when selected on page 2
    if (this.page1.roblast && this.oblasts) {
      const obl = this.oblasts.find(x => x.Id == this.page1.roblast);
      if (obl) {
        this.addressData.roblast = obl.r_region;
        this.recipientOblastReadonly = true;
      }
    }

    // Determine whether recipient email is mandatory
    const rc = this.countries.find(x => x.CountryCodeValue == this.page1.rcountrycode || x.r_country == this.page1.rcountry);
    this.recipientEmailRequired = !!(rc && rc.ValidationRequired);

    // Pre-fill sender fields from logged-in user profile
    if (this.dataService.userData.UserData.u_type == 'CUSTOMER') {
      for (const key in this.sender_map) {
        let val = this.dataService.userData.UserData[this.sender_map[key]] || '';
        if (key == 'scountry' && val) val = val.toUpperCase();
        this.addressData[key] = val;
      }
      this.onScountryChange();
      // Normalise stored province abbreviation
      const sc = this.addressData.scountry;
      const cl = this.countryStates ? this.countryStates[sc] : null;
      if (cl && this.addressData.soblast) {
        const v = this.addressData.soblast.toUpperCase();
        const sn = cl.find(x => x.state == v || x.desc.toUpperCase() == v);
        if (sn) this.addressData.soblast = sn.state;
      }
    }

    // Attach Google Maps autocomplete once DOM elements are rendered
    setTimeout(() => {
      initAutocomplete_s(this.updateStateList.bind(this));
      initAutocomplete(this.updateStateList.bind(this));
      restrictCountry(this.page1.rcountry);
    }, 300);
  }

  private nextPage() {
    const page = this.pageno - 1;
    setTimeout(() => {
      this.pageno++;
      setTimeout(() => {
        this.animationState[page] = this.animationState[page] === 'out' ? 'in' : 'out';
        this.animationState[page + 1] = this.animationState[page + 1] === 'out' ? 'in' : 'out';
        $('html, body').animate({ scrollTop: '0px' }, 300);
      });
    });
  }

  private skipPages(n: number) {
    const page = this.pageno - 1;
    setTimeout(() => {
      this.pageno += n;
      setTimeout(() => {
        this.animationState[page] = this.animationState[page] === 'out' ? 'in' : 'out';
        this.animationState[page + n] = this.animationState[page + n] === 'out' ? 'in' : 'out';
        $('html, body').animate({ scrollTop: '0px' }, 300);
      });
    });
  }

  // ── AG Grid helpers (unchanged) ────────────────────────────────────────────
  currencyFormatter(params) {
    return params.value ? params.value.toFixed(2) : '';
  }

  onGrid2Ready(params) {
    this.grid2Api = params.api;
    this.grid2Api.setRowData(this.rowData2);
    params.columnApi.autoSizeColumns();
    this.grid2Api.startEditingCell({ rowIndex: 0, colKey: 'Col1' });
  }

  autoSizeAll(colapi: any) {
    const allColumnIds = [];
    colapi.getAllColumns().forEach(column => { allColumnIds.push(column.colId); });
    colapi.autoSizeColumns(allColumnIds);
  }

  addRow() {
    this.rowData2.push({ groupname: '', description: '', weight: '', value: '', quantity: '', seqno: this.rowData2.length });
    this.grid2Api.setRowData(this.rowData2);
  }

  deleteRow() {
    const row = this.grid2Api.getSelectedRows()[0];
    if (!row) return;
    const ix = row['seqno'];
    this.rowData2.splice(ix, 1);
    for (let i = 0; i < this.rowData2.length; i++) this.rowData2[i]['seqno'] = i;
    this.grid2Api.setRowData(this.rowData2);
  }

  hasRows() {
    return this.rowData2.some(x => x.groupname !== '' && x.quantity != '');
  }

  onDiscount(id) {
    if (id > 0) this.discountID = id;
    this.submitted('grid_d');
  }

  // ── Build the final Parcel model (unchanged) ───────────────────────────────
  buildModel(p1: any, rates: any, address: any, p4: any, p5: any): Parcel {
    const model = new Parcel();
    model.id = 0;
    model.userID = this.dataService.userData.UserData.id;
    model.branchID = this.dataService.userData.Branch.id;
    model.source = this.dataService.userData.UserData.u_email;
    model.customerCD = this.dataService.userData.UserData.customerCD;
    model.currencyCD = 'USD';
    model.TotalCustomerOwe_USD = p1.amount;
    model.DeliveryMode = rates.DeliveryModeCode;
    model.RateName = rates.TransferMode;
    model.value = rates.TotalCustomerOwe_CAD;
    model.vol_fee = rates.volume_fee;
    model.vol_wght = rates.VolumeWeight;
    model.rate = rates.rate;
    model.rate_fee = rates.rate_fee;
    model.delivery = rates.delivery;
    model.rate_fee_meest = rates.rate_fee_meest;
    model.rate_fee_agent = rates.rate_fee_agent;
    model.rate_fee_ws = rates.rate_fee_ws;
    model.delivery_meest = rates.delivery_meest;
    model.delivery_agent = rates.delivery_agent;
    model.delivery_ws = rates.delivery_ws;
    model.ins_meest = rates.ins_meest;
    model.ins_agent = rates.ins_agent;
    model.ins_ws = rates.ins_ws;
    model.Packaging_meest = rates.Packaging_meest;
    model.Packaging_agent = rates.Packaging_agent;
    model.Packaging_ws = rates.Packaging_ws;
    model.s_phone = address.sphone;
    model.s_email = address.semail;
    model.s_apart = address.sapartn;
    model.s_city = address.scity;
    model.s_country = address.scountry;
    model.s_firstnm = address.sfname;
    model.s_house = address.sstreetn;
    model.s_index = address.szip;
    model.s_oblast = address.soblast;
    model.s_lastnm = address.slname;
    model.s_region = address.sregion;
    model.s_street = address.sstreet;
    model.r_phone = address.rphone;
    model.r_email = address.remail;
    model.r_apart = address.rapartn;
    model.r_city = address.rcity;
    model.r_country = address.rcountry;
    model.r_CountryCode = p1.rcountrycode;
    model.r_firstnm = address.rfname;
    model.r_house = address.rstreetn;
    model.r_index = address.rzip;
    model.r_oblast = address.roblast;
    model.r_lastnm = address.rlname;
    model.r_region = address.rregion;
    model.r_street = address.rstreet;
    model.notes = p4.notes;
    model.code_a = (!p4.declaration || p4.declaration == '') ? null : p4.declaration;
    model.DiscountID = this.discountID;
    model.MT_Occupation = this.complianceData.occupation;
    model.MT_OccupationOther = this.complianceData.occupationOther;
    model.MT_TrPurp = this.complianceData.tr_purp;
    model.MT_Thdprttr = this.complianceData.thdprttr;
    model.MT_Onbehalfof = this.complianceData.onbehalfof;
    model.MT_Sourcefund = this.complianceData.sourcefund;
    return model;
  }

  // ── Quagga barcode scanner (unchanged logic, model update only) ────────────
  startQuagga() {
    const me = this;
    Quagga.init({
      inputStream: { name: 'Live', type: 'LiveStream' },
      decoder: { readers: ['code_128_reader'] }
    }, function(err) {
      if (err) { console.log(err); me.showScanner = false; alert(err); return; }
      Quagga.start();
    });
  }

  setupQuagga() {
    Quagga.onProcessed((result) => {
      const drawingCtx = Quagga.canvas.ctx.overlay;
      const drawingCanvas = Quagga.canvas.dom.overlay;
      if (result) {
        if (result.boxes) {
          drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute('width')), parseInt(drawingCanvas.getAttribute('height')));
          result.boxes.filter(box => box !== result.box).forEach(box => {
            Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, { color: 'green', lineWidth: 2 });
          });
        }
        if (result.box)
          Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: '#00F', lineWidth: 2 });
        if (result.codeResult && result.codeResult.code)
          Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, { color: 'red', lineWidth: 3 });
      }
    });

    Quagga.onDetected((result) => {
      this.reviewData.declaration = result.codeResult.code;
      this.scannedData = result.codeResult.code;
      Quagga.stop();
      this.showScanner = false;
    });
  }
}
